import * as Yup from 'yup';
import { ABSOLUTE, CHARGE, PERCENTAGE } from '../constants/defaults';
import { isOutOfScopeDocumentType } from '../utils/documentTypeRules';
import { DEFAULT_VALIDATION_MESSAGES } from '../hooks/useTranslatedMessages';

const defaultMessages = DEFAULT_VALIDATION_MESSAGES?.allowance_charge ?? {};

export const createAllowanceChargeSchema = (
  currentNetAmount,
  itemBaseAmount,
  type,
  isTypeValueValidationNeeded = true,
  isForAllowanceForm = true,
  isDocumentLevel = false,
  invoiceTypeCode = null,
  messages = defaultMessages,
) => {
  const m = messages;

  const isOutOfScopeDocument = (ctx) => {
    const contextType =
      ctx?.options?.context?.invoice_type_code ?? ctx?.options?.context?.credit_note_type_code ?? null;
    const rootValue =
      ctx?.from && Array.isArray(ctx.from) && ctx.from.length > 0 ? ctx.from[ctx.from.length - 1]?.value : null;
    const rootType = rootValue?.invoice_type_code ?? rootValue?.credit_note_type_code ?? null;
    const effectiveDocumentTypeCode = invoiceTypeCode || contextType || rootType;

    return isOutOfScopeDocumentType(effectiveDocumentTypeCode);
  };

  return Yup.object({
    base_amount: Yup.number()
      .nullable()
      .when('type', {
        is: PERCENTAGE,
        then: (schema) =>
          schema
            .transform((value, originalValue) => {
              if (!originalValue) return null;
              return value;
            })
            .required(m.base_amount_required)
            .min(0, m.base_amount_must_be_positive)
            .max(itemBaseAmount, m.base_amount_cannot_exceed.replace('{max}', itemBaseAmount)),
        otherwise: (schema) => schema,
      }),
    ...(isTypeValueValidationNeeded && {
      type: Yup.string().oneOf([PERCENTAGE, ABSOLUTE]).required(m.type_required),
      value: Yup.number()
        .transform((value, originalValue) => {
          if (!originalValue) return null;
          return value;
        })
        .required(m.value_required)
        .min(0, m.value_must_be_positive)
        .when('type', {
          is: PERCENTAGE,
          then: (schema) => {
            const negMsg = m.negative_net_amount_not_allowed;
            if (type === CHARGE) {
              return schema.min(0.01, m.percentage_min_001).test('negative-net-amount', negMsg, function (value) {
                if (value && this.parent.base_amount) {
                  const calculatedAmount = (this.parent.base_amount * value) / 100;
                  const newNetAmount = currentNetAmount + calculatedAmount;
                  return newNetAmount >= 0;
                }
                return true;
              });
            } else {
              return schema
                .min(1, m.percentage_min_one)
                .max(100, m.percentage_max_hundred)
                .test('max-amount', m.allowance_amount_cannot_exceed_total, function (value) {
                  if (value && this.parent.base_amount && currentNetAmount) {
                    const calculatedAmount = (this.parent.base_amount * value) / 100;
                    return calculatedAmount <= currentNetAmount;
                  }
                  return true;
                })
                .test('negative-net-amount', negMsg, function (value) {
                  if (value && this.parent.base_amount) {
                    const calculatedAmount = (this.parent.base_amount * value) / 100;
                    const newNetAmount = currentNetAmount - calculatedAmount;
                    return newNetAmount >= 0;
                  }
                  return true;
                });
            }
          },
          otherwise: (schema) => {
            const negMsg = m.negative_net_amount_not_allowed;
            if (type === CHARGE) {
              return schema.test('negative-net-amount', negMsg, function (value) {
                if (value) {
                  const newNetAmount = currentNetAmount + value;
                  return newNetAmount >= 0;
                }
                return true;
              });
            } else {
              return schema
                .max(currentNetAmount, m.value_cannot_exceed_net.replace('{max}', currentNetAmount))
                .test('negative-net-amount', negMsg, function (value) {
                  if (value) {
                    const newNetAmount = currentNetAmount - value;
                    return newNetAmount >= 0;
                  }
                  return true;
                });
            }
          },
        }),
    }),

    ...(isForAllowanceForm && {
      reason_code: Yup.string()
        .nullable()
        .test('reason-required', m.reason_code_or_custom_required, function (value) {
          const { reason } = this.parent;
          return !!(value || reason);
        }),

      reason: Yup.string()
        .nullable()
        .test('reason-required', m.reason_code_or_custom_required, function (value) {
          const { reason_code } = this.parent;
          return !!(value || reason_code);
        }),
    }),

    ...(isDocumentLevel && {
      tax_category: Yup.object()
        .shape({
          id: Yup.string().required(m.tax_category_required_document_level),
          percent: Yup.number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .when('id', {
              is: (id) => id === 'E' || id === 'O',
              then: (schema) => schema.notRequired().default(0),
              otherwise: (schema) => schema.nullable(),
            })
            .min(0, m.tax_rate_positive)
            .max(100, m.tax_rate_max_hundred)
            .test('out-of-scope-percent-zero', m.out_of_scope_tax_rate_must_be_zero, function (value) {
              if (!isOutOfScopeDocument(this)) {
                return true;
              }

              return Number(value) === 0;
            }),
          tax_exemption_reason_code: Yup.string()
            .nullable()
            .test('tax-exemption-reason-code-required', m.tax_exemption_reason_code_required_for_e, function (value) {
              const { id } = this.parent;
              if (id === 'E') {
                return value !== null && value !== undefined && value !== '';
              }
              return true;
            }),

          tax_exemption_reason: Yup.string().nullable(),
        })
        .test('out-of-scope-tax-category', m.out_of_scope_allowed_tax_categories, function (value) {
          if (!isOutOfScopeDocument(this)) {
            return true;
          }

          return ['E', 'O', 'Z'].includes(value?.id || '');
        })
        .test(
          'document-level-not-n-category',
          type === CHARGE ? m.document_level_charge_cannot_use_n : m.document_level_allowance_cannot_use_n,
          function (value) {
            if (!value?.id) {
              return true;
            }

            return value.id !== 'N';
          },
        )
        .required(),
    }),
  });
};
